/**
 * Loads the Google Maps JavaScript API from the browser (no PHP enqueue).
 *
 * Uses a private loader callback — map init stays in the ESM module, not global.
 *
 * @param {string} apiKey Restricted browser API key from sumy_map.google_api_key.
 * @return {Promise<typeof google.maps>} Resolves when the API is ready.
 */
export function loadGoogleMaps( apiKey ) {
	if ( ! apiKey ) {
		return Promise.reject(
			new Error( 'Missing Google Maps API key (sumy_map.google_api_key).' )
		);
	}

	if ( typeof window.google?.maps?.importLibrary === 'function' ) {
		return Promise.resolve( window.google.maps );
	}

	return new Promise( ( resolve, reject ) => {
		const callbackName = '__sumyMapsApiReady';

		window[ callbackName ] = () => {
			delete window[ callbackName ];
			resolve( window.google.maps );
		};

		const params = new URLSearchParams( {
			key: apiKey,
			v: 'weekly',
			loading: 'async',
			libraries: 'maps,marker',
			callback: callbackName,
		} );

		const script = document.createElement( 'script' );
		script.src = `https://maps.googleapis.com/maps/api/js?${ params.toString() }`;
		script.async = true;
		script.onerror = () => {
			delete window[ callbackName ];
			reject( new Error( 'The Google Maps JavaScript API could not load.' ) );
		};

		const nonceEl = document.querySelector( 'script[nonce]' );
		if ( nonceEl?.nonce ) {
			script.nonce = nonceEl.nonce;
		}

		document.head.append( script );
	} );
}
